/*
 * Application navigation, page display controls and document-level presenters.
 * App supplies route and authentication state; the shell retains chrome through portal movement.
 * Example: registerAppShell(registry); mount tbc-app-shell with the application context.
 */

import { Toasts } from '../ui/toasts.jsx';
import { ThemeDock } from '../theming/theme-dock.jsx';
import { BodyCover } from '../theming/body-cover.jsx';
import { ConversationPopup } from '../../views/messaging/conversation-popup.jsx';
import { ConversationDialog } from '../../views/messaging/conversation-dialog.jsx';
import { UIElement, defineUIElements } from '../../runtime/components/ui-element.js';
import { Icon } from '../../runtime/components/presentation.jsx';
import { AppNavigation, registerComponents as registerNavigation } from './app-navigation.jsx';
import { AccountLayers, registerComponents as registerAccounts } from '../accounts/root-layers.jsx';
import { registerComponents as registerThemeDock } from '../theming/theme-dock.jsx';
import { registerComponents as registerBodyCover } from '../theming/body-cover.jsx';
import { registerComponents as registerToasts } from '../ui/toasts.jsx';
import { registerConversationPopup } from '../../views/messaging/conversation-popup.jsx';
import { registerConversationDialog } from '../../views/messaging/conversation-dialog.jsx';

export class AppShell extends UIElement {
  static properties = {
    navigation: { attribute: false },
    navigationActions: { attribute: false },
    onSkip: { attribute: false },
    onWidth: { attribute: false },
    onArtwork: { attribute: false },
    onClosePopup: { attribute: false },
    onCommit: { attribute: false },
    closeDisclosures: { attribute: false },
    admire: { state: true },
    mainInert: { state: true },
    mapStage: { state: true },
    popup: { state: true },
    messageDialog: { state: true },
    attribution: { state: true },
    artworkHidden: { state: true },
    expanded: { state: true },
    large: { state: true },
    docked: { state: true },
    small: { state: true },
    routeClass: { attribute: false }
  };

  constructor() {
    super();
    this.navigation = { kind: 'loading' };
    this.navigationActions = {};
    this.admire =
      this.mainInert =
      this.mapStage =
      this.expanded =
      this.large =
      this.small =
      this.docked =
        false;
    this.artworkHidden = true;
    this.popup = this.messageDialog = this.attribution = null;
    this.captureMain = (node) => (this.main = node);
    this.captureNav = (node) => (this.navigationNode = node);
    this.captureControls = (node) => (this.controlsNode = node);
    this.captureWidth = (node) => (this.widthButton = node);
    this.captureArtwork = (node) => (this.artworkButton = node);
    this.captureAttribution = (node) => (this.attributionNode = node);
    this.capturePopup = (node) => (this.popupNode = node);
    this.captureDialog = (node) => (this.dialogNode = node);
    this.bindMethods('syncChrome', 'park');
    this.own(() => this.releaseDestination?.());
  }

  willUpdate(changed) {
    super.willUpdate(changed);
    if (this.widthButton === this.context?.document.activeElement)
      this.restoreControl = this.widthButton;
    if (changed.has('admire'))
      this.restoreControl =
        this.closeDisclosures?.(this.navigationNode, this.restoreControl) || this.restoreControl;
  }

  updated(changed) {
    super.updated(changed);
    const life = this.connection;
    if (life && life !== this.observedConnection) {
      this.observedConnection = life;
      const { document, window } = this.context;
      const observer = new window.MutationObserver(life.guard(this.syncChrome));
      observer.observe(this.main, { childList: true, subtree: true });
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      life.own(() => observer.disconnect());
      const focus = life.guard((event) => {
        if (this.navigationNode.contains(event.target)) this.focusedControl = event.target;
        else if (event.target !== document.body && event.target !== document.documentElement)
          this.focusedControl = null;
      });
      const pointer = life.guard((event) => {
        if (!this.navigationNode.contains(event.target)) this.focusedControl = null;
      });
      document.addEventListener('focusin', focus);
      document.addEventListener('pointerdown', pointer, true);
      life.own(() => document.removeEventListener('focusin', focus));
      life.own(() => document.removeEventListener('pointerdown', pointer, true));
      life.own(() => delete document.documentElement.dataset.displayMode);
    }

    this.syncChrome();
    const root = this.context.document.documentElement;
    const presentation = this.admire
      ? 'artwork'
      : (this.large ? this.expanded : this.small)
        ? 'full'
        : 'normal';
    if (root.dataset.displayMode !== presentation) root.dataset.displayMode = presentation;
    const target = this.restoreControl;
    this.restoreControl = null;
    if (target && !this.admire) {
      const active = this.context.document.activeElement;
      if (target === this.widthButton && target.hidden) {
        const replacement = [
          ...this.controlsNode.querySelectorAll('button:not([hidden])'),
          ...this.navigationNode.querySelectorAll('a, button')
        ];
        replacement.some((node) => this.context.services.a11y.focusElement(node));
      } else if (
        this.navigationNode.contains(target) &&
        (active === this.context.document.body || active === root || active === target)
      ) {
        this.context.services.a11y.focusElement(target);
      }
    }
    this.onCommit?.();
  }

  syncChrome() {
    if (!this.main || this.disposed) return;
    const { document } = this.context;
    const root = document.documentElement;
    const expanded = root.classList.contains('content-expanded');
    const large = root.classList.contains('layout-lg');
    const small = root.classList.contains('layout-sm');
    const next = this.parked
      ? null
      : [...this.main.querySelectorAll('[data-page-chrome-actions]')].find(
          (node) => !node.closest('dialog, [role="dialog"], .conversation-popup, [hidden], .hidden')
        ) || null;
    const destinationChanged = next !== this.destination;
    if (destinationChanged) {
      const active = document.activeElement;
      if (active === document.body || active === root || active === this.focusedControl)
        this.restoreControl = this.focusedControl;
      this.releaseDestination?.();
      this.destination = next;
      this.releaseDestination = next ? this.registerDestination('page-actions', next) : null;
    }
    const docked = (large ? expanded : small) && !this.admire && !!next;
    if (
      this.docked !== docked ||
      this.large !== large ||
      this.expanded !== expanded ||
      (destinationChanged && docked)
    )
      this.restoreControl =
        this.closeDisclosures?.(this.navigationNode, this.restoreControl) || this.restoreControl;
    this.small = small;
    this.large = large;
    this.expanded = expanded;
    this.docked = docked;
  }

  async park() {
    this.parked = true;
    this.closeDisclosures?.(this.navigationNode);
    this.releaseDestination?.();
    this.releaseDestination = null;
    this.destination = null;
    this.docked = false;
    await this.whenRendered();
  }

  resume() {
    this.parked = false;
    this.syncChrome();
  }

  renderControls() {
    return (
      <div ref={this.captureControls} class="view-controls" role="group" aria-label="Page display" hidden={this.admire || (!this.large && this.artworkHidden)}>
        <button ref={this.captureWidth} type="button" class="nav-icon-button content-width-toggle" hidden={!this.large} title={this.expanded ? 'Shrink content width' : 'Expand content width'} aria-label={this.expanded ? 'Shrink content width' : 'Expand content width'} aria-pressed={String(this.expanded)} onClick={this.onWidth}>
          <Icon name={this.expanded ? 'fold-horizontal' : 'unfold-horizontal'} classes="icon-sm"/>
        </button>
        <button ref={this.captureArtwork} type="button" class="nav-icon-button" hidden={this.artworkHidden} title="Show background artwork" aria-label="Show background artwork" aria-pressed={String(this.admire)} onClick={this.onArtwork}>
          <Icon name="maximize" classes="icon-sm"/>
        </button>
      </div>
    );
  }

  renderAttribution() {
    const value = this.attribution;
    return (
      <div ref={this.captureAttribution} class="admire-attribution" data-contrast={value.textColor === '#ffffff' ? 'light' : 'dark'}>
        {value.credit ? <span>{value.credit}</span> : null}
        {value.credit && value.assetURL ? <span aria-hidden="true">·</span> : null}
        {value.assetURL ? (
          <a
            href={value.assetURL}
            target="_blank"
            rel="noopener noreferrer"
            class="admire-original-link">
            Open original
          </a>
        ) : null}
      </div>
    );
  }

  renderNavigation() {
    return (
      <AppNavigation
        state={this.navigation}
        actions={this.navigationActions}
        controls={this.renderControls()}
        context={this.context}/>
    );
  }

  render() {
    const navigation = (
      <nav
        commandScope
        ref={this.captureNav}
        id="app-nav"
        class={`app-shell-nav${this.docked ? ' app-nav-docked' : ''}`}
        hidden={this.admire}>
        {this.renderNavigation()}
      </nav>
    );
    return (
      <>
        <a id="skip-content" class="skip-content" href="#main-content" onClick={this.onSkip}>
          Skip to content
        </a>
        <div class="app-navigation-home" ref={this.outlet('navigation-home')}/>
        {this.portal(
          'application-navigation',
          this.docked ? 'page-actions' : 'navigation-home',
          navigation
        )}
        <Toasts context={this.context}/>
        <main
          ref={this.captureMain}
          id="main-content"
          class={[
            'app-shell-main',
            this.routeClass,
            this.admire && 'admire-hidden',
            this.mapStage && 'map-stage-active'
          ]
            .filter(Boolean)
            .join(' ')}
          tabindex="-1"
          inert={this.mainInert}/>
        <ThemeDock context={this.context}/>
        <BodyCover context={this.context}/>
        <AccountLayers context={this.context}/>
        <div id="a11y-live-polite" class="sr-only" aria-live="polite" aria-atomic="true"/>
        <div id="a11y-live-assertive" class="sr-only" aria-live="assertive" aria-atomic="true"/>
        {this.attribution
          ? this.portal('artwork-attribution', 'body', this.renderAttribution())
          : null}
        {this.popup
          ? this.portal(
              'conversation-popup',
              'body',
              <ConversationPopup
                key={this.popup.session}
                ref={this.capturePopup}
                class="conversation-popup"
                tabindex="-1"
                context={this.context}
                conversationId={this.popup.conversationId}
                recipientId={this.popup.to}
                onClose={this.onClosePopup}
                setInterests={this.popup.setInterests}/>
            )
          : null}
        {this.messageDialog ? (
          <ConversationDialog
            key={this.messageDialog.session}
            ref={this.captureDialog}
            context={this.context}
            kind={this.messageDialog.kind}
            callerSignal={this.messageDialog.callerSignal}
            logicalOwner={this.messageDialog.logicalOwner}
            onClose={this.messageDialog.close}/>
        ) : null}
      </>
    );
  }
}

export function registerAppShell(registry) {
  registerNavigation(registry);
  registerAccounts(registry);
  registerThemeDock(registry);
  registerBodyCover(registry);
  registerToasts(registry);
  registerConversationPopup(registry);
  registerConversationDialog(registry);
  defineUIElements(registry, { 'tbc-app-shell': AppShell });
}
